import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorMessage } from "@hookform/error-message";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useMakeRequest from "../../utils/ApiHelper";
import { ApiTypes } from "../../constants/Constants";
import { ApiConstants } from "../../constants/ApiConstants";
import addressValidation from "../../Validation/AddressValidaton";
import FrontLoader from "../layout/FrontLoader";
import LocationInput from "./LocationInput";
import { LanguageConstants } from "../../constants/LanguageConstants";
import { attachAddressLine2ForApi } from "../../utils/addressPayload";
import { buildSortedShippingDisplayRows } from "../../utils/shippingDisplay";
import { isProfileBusinessAccount, isProfileIndividualAccount } from "../../utils/profileAccountType";
import { Routes } from "../../constants/RouteConstants";

interface FormInputs {
  street_line: string;
  appartment: string;
  state: string;
  city: string;
  zip_code: string;
  loaction_type: string;
  instructions: string;
}

function NewAddressPage() {
  const navigate = useNavigate();
  const profileState = useSelector((state: any) => state?.ProfileState);
  const isBusiness = isProfileBusinessAccount(profileState);
  const isIndividual = isProfileIndividualAccount(profileState);
  /** Business: billing-only checkbox. Individual: primary + billing. Other (e.g. legacy): primary only. */
  const showBillingSameCheckbox = isBusiness || isIndividual;
  const showPrimaryAddressCheckbox = !isBusiness;

  const addressAdd = useMakeRequest();
  const shippingListRequest = useMakeRequest();
  /** `ship_method_trans.ref_id` as string (same as quotation / order / UpdateAddressPage) */
  const [shippingId, setShippingId] = useState("");
  const [isPrimaryAddress, setIsPrimaryAddress] = useState(false);
  const [isBillingSame, setIsBillingSame] = useState(false);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [country, setCountry] = useState("");

  const shippingRows = useMemo(() => {
    return buildSortedShippingDisplayRows(shippingListRequest?.data?.data).map(({ refId, label }) => ({
      refId,
      name: label,
    }));
  }, [shippingListRequest?.data?.data]);

  useEffect(() => {
    shippingListRequest.makeRequest({ url: ApiConstants?.ShippingList, toastStatus: false });
  }, []);

  useEffect(() => {
    if (!showBillingSameCheckbox) {
      setIsBillingSame(false);
    }
  }, [showBillingSameCheckbox]);

  useEffect(() => {
    if (!showPrimaryAddressCheckbox) {
      setIsPrimaryAddress(false);
    }
  }, [showPrimaryAddressCheckbox]);

  const {
    register,
    watch,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInputs>({
    mode: "onChange",
    resolver: yupResolver(addressValidation),
    defaultValues: {
      street_line: "",
      appartment: "",
      state: "",
      city: "",
      zip_code: "",
      loaction_type: "1",
      instructions: "",
    },
  });

  useEffect(() => {
    if (addressAdd.status) {
      navigate(Routes.AddressList);
    }
  }, [addressAdd.status, navigate]);

  const handleLocationSuccess = (result: any) => {
    setLatitude(result?.latitudeLongitude?.lat ?? null);
    setLongitude(result?.latitudeLongitude?.lng ?? null);
    setValue("street_line", result?.formattedAddress || "");
    setValue("city", result?.city || "");
    setValue("state", result?.state || "");
    setCountry(result?.country || "");
    if (result?.zip_code) {
      setValue("zip_code", result.zip_code);
    }
  };

  const onSubmit = (body: FormInputs) => {
    const shipping_id =
      shippingId != null && String(shippingId).trim() !== "" ? Number(shippingId) : null;
    const payload = attachAddressLine2ForApi({
      ...body,
      country,
      lat: latitude,
      long: longitude,
      shipping_id: Number.isFinite(shipping_id as number) && (shipping_id as number) > 0 ? shipping_id : null,
      is_primary: showPrimaryAddressCheckbox && isPrimaryAddress ? 1 : 0,
      billing_same_as_shipping: showBillingSameCheckbox && isBillingSame ? 1 : 0,
    });
    addressAdd.makeRequest({
      payload,
      type: ApiTypes.POST,
      url: ApiConstants?.AddressAdd,
      toastStatus: true,
    });
  };

  return (
    <>
      {addressAdd.loading || shippingListRequest.loading ? <FrontLoader /> : null}
      <div className="head-space">
        <div className="inner-content">
          <div className="container">
            <div className="address-book-shell address-book-shell--form">
              <span className="address-book-badge">Address Book</span>
              <form onSubmit={handleSubmit(onSubmit)} className="address-form-page">
                <div className="address-form-section">
                  <h2 className="address-form-section__heading">Delivery Address</h2>
                  <div className="row g-3">
                    <div className="col-lg-4">
                      <div className="address-form-field">
                        <label className="address-form-label">Address Line 1</label>
                        <LocationInput
                          id="new-address-line-1"
                          className="form-control address-form-input"
                          onSuccess={handleLocationSuccess}
                          value={watch("street_line")}
                          placeholder="Enter address"
                          onChange={(e) => setValue("street_line", e?.target?.value)}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="street_line"
                          render={({ message }) => <p className="text-error-new">{message}</p>}
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="address-form-field">
                        <label className="address-form-label">Address Line 2</label>
                        <input
                          type="text"
                          className="form-control address-form-input"
                          placeholder="Enter address"
                          {...register("appartment")}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="appartment"
                          render={({ message }) => <p className="text-error-new">{message}</p>}
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="address-form-field">
                        <label className="address-form-label">{LanguageConstants?.INSTRUCTIONS}</label>
                        <input
                          type="text"
                          className="form-control address-form-input"
                          placeholder="Enter instructions"
                          {...register("instructions")}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="instructions"
                          render={({ message }) => <p className="text-error-new">{message}</p>}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="address-form-divider"></div>

                <div className="address-form-section">
                  <h2 className="address-form-section__heading">Type of Residence</h2>
                  <p className="address-form-section__subheading">Select Residence Type</p>
                  <div className="address-form-options">
                    <label className="address-form-option">
                      <input type="radio" value="1" {...register("loaction_type")} />
                      <span>{LanguageConstants?.RESIDENTIAL}</span>
                    </label>
                    <label className="address-form-option">
                      <input type="radio" value="2" {...register("loaction_type")} />
                      <span>{LanguageConstants?.ACCOUNT_TYPE_BUSINESS || "Business"}</span>
                    </label>
                    <label className="address-form-option">
                      <input type="radio" value="3" {...register("loaction_type")} />
                      <span>Industrial</span>
                    </label>
                  </div>
                  <ErrorMessage
                    errors={errors}
                    name="loaction_type"
                    render={({ message }) => <p className="text-error-new mt-2">{message}</p>}
                  />
                </div>

                <div className="address-form-divider"></div>

                <div className="address-form-section">
                  <h2 className="address-form-section__heading">Shipping Method</h2>
                  <p className="address-form-section__subheading">Select Shipping Method</p>
                  <div className="address-form-options">
                    {shippingRows.length > 0 ? (
                      shippingRows.map((item) => (
                        <label className="address-form-option" key={item.refId}>
                          <input
                            type="radio"
                            name="shipping_id"
                            value={item.refId}
                            checked={shippingId === item.refId}
                            onChange={(e) => setShippingId(e.target.value)}
                          />
                          <span>{item.name}</span>
                        </label>
                      ))
                    ) : (
                      <p className="text-muted small mb-0">Loading shipping methods…</p>
                    )}
                  </div>
                </div>
                <br/>

                <div className="address-form-checks">
                  {showPrimaryAddressCheckbox ? (
                    <label className="address-form-check">
                      <input
                        type="checkbox"
                        checked={isPrimaryAddress}
                        onChange={(e) => setIsPrimaryAddress(e.target.checked)}
                      />
                      <span>{LanguageConstants?.BULK_SET_PRIMARY_ADDRESS}</span>
                    </label>
                  ) : null}
                  {showBillingSameCheckbox ? (
                    <label className="address-form-check">
                      <input
                        type="checkbox"
                        checked={isBillingSame}
                        onChange={(e) => setIsBillingSame(e.target.checked)}
                      />
                      <span>{LanguageConstants?.MANUAL_QUOTE_BILLING_SAME}</span>
                    </label>
                  ) : null}
                </div>

                <div className="address-form-actions">
                  <button type="submit" className="manual-quote-btn com-btn rounded-pill">
                    {LanguageConstants?.SAVE}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewAddressPage;
